import { Platform } from "react-native";
import { File } from "expo-file-system";
import * as Sentry from "@sentry/react-native";
import { supabase } from "@lezz/supabase";
import { decode } from "base64-arraybuffer";
import { logger } from "@/shared/lib/logger";

const BUCKET_NAME = "avatars";
const DEFAULT_MIME_TYPE = "image/jpeg";
const DEFAULT_EXTENSION = "jpg";

/** Maps file extensions to MIME types */
const EXTENSION_TO_MIME: Record<string, string> = {
  png: "image/png",
  webp: "image/webp",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
};

/** Maps MIME types to file extensions */
const MIME_TO_EXTENSION: Record<string, string> = {
  "image/png": "png",
  "image/webp": "webp",
  "image/jpeg": "jpg",
};

/**
 * Gets MIME type from a file URI based on its extension
 */
function getMimeTypeFromUri(uri: string): string {
  const extension = uri.split(".").pop()?.toLowerCase();
  return (extension && EXTENSION_TO_MIME[extension]) || DEFAULT_MIME_TYPE;
}

/**
 * Gets file extension from a MIME type
 */
function getExtensionFromMimeType(mimeType: string): string {
  return MIME_TO_EXTENSION[mimeType] || DEFAULT_EXTENSION;
}

/**
 * Creates a blob from a web blob URL
 */
async function createBlobFromWebUrl(
  blobUrl: string
): Promise<{ data: Blob; mimeType: string }> {
  const response = await fetch(blobUrl);
  if (!response.ok) {
    throw new Error(`Failed to fetch image: ${response.statusText}`);
  }
  const blob = await response.blob();
  const mimeType = blob.type || DEFAULT_MIME_TYPE;
  return { data: blob, mimeType };
}

/**
 * Creates file data from a native file URI using expo-file-system/next
 * For React Native: returns ArrayBuffer (Supabase accepts ArrayBuffer directly)
 * For Web: returns Blob (for consistency with web blob URLs)
 * Uses base64-arraybuffer for Hermes runtime compatibility where atob() may not work
 */
async function createFileDataFromNativeUri(
  fileUri: string
): Promise<{ data: Blob | ArrayBuffer; mimeType: string }> {
  const file = new File(fileUri);
  const base64 = await file.base64();
  const mimeType = getMimeTypeFromUri(fileUri);
  const arrayBuffer = decode(base64);

  // On React Native, pass ArrayBuffer directly (Supabase accepts it)
  // On Web, wrap in Blob for consistency
  if (Platform.OS === "web") {
    const blob = new Blob([arrayBuffer], { type: mimeType });
    return { data: blob, mimeType };
  }

  return { data: arrayBuffer, mimeType };
}

/**
 * Generates a unique file path for avatar storage
 */
function generateAvatarFilePath(
  userId: string,
  imageUri: string,
  mimeType: string
): string {
  const extension = getExtensionFromMimeType(mimeType);
  const imageUriId = imageUri.split("/").pop() || "avatar";
  return `${userId}/${imageUriId}.${extension}`;
}

/** Public URL path prefix for the avatars bucket (environment-agnostic) */
const STORAGE_PUBLIC_PATH_PREFIX = `/storage/v1/object/public/${BUCKET_NAME}/`;

/**
 * Uploads an avatar image to Supabase storage
 * @param userId - The user ID to associate with the avatar
 * @param imageUri - Local file URI from expo-image-picker (or blob URL on web)
 * @returns Storage path (e.g. /storage/v1/object/public/avatars/userId/file.jpg) so the app can build the full URL with the current origin
 */
export async function uploadAvatar(
  userId: string,
  imageUri: string
): Promise<string> {
  return Sentry.startSpan(
    { name: "avatar-upload", op: "storage.upload" },
    async (span) => {
      span.setAttribute("bucket", BUCKET_NAME);
      span.setAttribute("userId", userId);

      const isWebBlob = imageUri.startsWith("blob:");

      const { data, mimeType } = isWebBlob
        ? await createBlobFromWebUrl(imageUri)
        : await createFileDataFromNativeUri(imageUri);

      const filePath = generateAvatarFilePath(userId, imageUri, mimeType);
      span.setAttribute("mimeType", mimeType);

      const { error } = await supabase.storage
        .from(BUCKET_NAME)
        .upload(filePath, data, {
          contentType: mimeType,
          upsert: true,
        });

      if (error) {
        throw new Error(`Failed to upload avatar: ${error.message}`);
      }

      return `${STORAGE_PUBLIC_PATH_PREFIX}${filePath}`;
    }
  );
}

/**
 * Extracts the bucket-relative file path from a storage path
 * (e.g. /storage/v1/object/public/avatars/userId/file.jpg → userId/file.jpg)
 */
function extractFilePath(path: string): string | null {
  const parts = path.split(`${STORAGE_PUBLIC_PATH_PREFIX}`);
  if (parts.length !== 2) return null;
  return parts[1] || null;
}

/**
 * Deletes an avatar image from Supabase storage
 * @param path - Storage path (e.g. /storage/v1/object/public/avatars/userId/file.jpg)
 */
export async function deleteAvatar(path: string): Promise<void> {
  const filePath = extractFilePath(path);

  if (!filePath) {
    return;
  }

  const { error } = await supabase.storage.from(BUCKET_NAME).remove([filePath]);

  if (error) {
    // Don't throw error if file doesn't exist - it's okay
    logger.warn("Failed to delete avatar", { error: error.message });
  }
}

/** Local file URI prefixes that indicate a file needs uploading */
const LOCAL_URI_PREFIXES = [
  "file://",
  "content://",
  "ph://",
  "blob:",
  "http://localhost",
];

/**
 * Checks if a URL is a local file URI (needs to be uploaded)
 * @param url - URL to check
 * @returns true if it's a local file URI or blob URL
 */
export function isLocalFileUri(url: string | null | undefined): boolean {
  if (!url) return false;
  return LOCAL_URI_PREFIXES.some((prefix) => url.startsWith(prefix));
}
